import axios, { AxiosHeaders } from "axios";
import { authService } from "../auth/services/AccountService";
import { config } from "@/utils/config";
import { dispatchCustomEvent, EVENTS } from "@/utils/events";
import { LogoutReasons } from "../auth/types";

export const apiClient = axios.create({
  baseURL: config.API_BASE_URL,
});

let refreshPromise: Promise<string | null> | null = null;

apiClient.interceptors.request.use(async (config) => {
  if (refreshPromise) {
    await refreshPromise;
  }

  const token = await authService.getAccessToken();
  if (!token) {
    return config;
  }

  config.headers.set("Authorization", `Bearer ${token}`);

  if (config.data instanceof FormData) {
    config.headers.set("Content-Type", "multipart/form-data");
  } else {
    config.headers.set("Content-Type", "application/json");
  }

  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const hasAuthError =
      error.response.status === 401 ||
      error.response.data?.error === "ERR_AUTHENTICATION_REQUIRED";

    if (
      hasAuthError &&
      !originalRequest._retry &&
      !originalRequest.url?.includes("/token")
    ) {
      originalRequest._retry = true;

      if (!refreshPromise) {
        refreshPromise = authService.getAccessToken();
      }

      try {
        const newToken = await refreshPromise;

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return apiClient(originalRequest);
        }

        await authService.clearTokens();
        dispatchCustomEvent(EVENTS.ACCOUNT_LOGOUT, LogoutReasons.TokenExpired);
        return Promise.reject(new axios.Cancel("Session expired"));
      } catch (refreshError) {
        await authService.clearTokens();
        dispatchCustomEvent(EVENTS.ACCOUNT_LOGOUT, LogoutReasons.TokenExpired);
        return Promise.reject(new axios.Cancel("Session expired"));
      } finally {
        refreshPromise = null;
      }
    }

    return Promise.reject(error);
  },
);
